import { useQuery } from '@tanstack/react-query';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { logisticsApi } from '../../../api/logistics.js';
import { t } from '../../../lib/i18n.js';

const TRANSFER_STATUS = { draft: ['Bozza', 'neutral'], requested: ['Richiesto', 'info'], in_transit: ['In transito', 'warning'], received: ['Ricevuto', 'success'], cancelled: ['Annullato', 'neutral'] };
const LOAD_STATUS = { draft: ['Bozza', 'neutral'], confirmed: ['Confermato', 'success'], cancelled: ['Annullato', 'neutral'] };

function storeName(s) { return s && (s.name || s.code) ? (s.name || s.code) : '-'; }
function fmtDate(d) { return d ? new Date(d).toLocaleString('it-IT') : '-'; }

export function LogisticsDetailModal({ open, onClose, kind, id }) {
  const isTransfer = kind === 'transfer';
  const query = useQuery({
    queryKey: [isTransfer ? 'transfer' : 'load', id],
    enabled: open && Boolean(id),
    queryFn: () => (isTransfer ? logisticsApi.getTransfer(id) : logisticsApi.getLoad(id))
  });
  const doc = query.data;
  const statusMap = isTransfer ? TRANSFER_STATUS : LOAD_STATUS;
  const [statusLabel, statusTone] = (doc && statusMap[doc.status]) || ['', 'neutral'];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isTransfer ? 'Dettaglio trasferimento' : 'Dettaglio carico'}
      size="lg"
      footer={<Button variant="ghost" onClick={onClose}>{t('common.close') || 'Chiudi'}</Button>}
    >
      {query.isLoading || !doc ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone={statusTone}>{statusLabel}</Badge>
            {isTransfer
              ? <span className="text-sm text-ink">{storeName(doc.fromStoreId)} → {storeName(doc.toStoreId)}</span>
              : <span className="text-sm text-ink">{storeName(doc.storeId)}{doc.supplier ? ` · ${doc.supplier}` : ''}{doc.reference ? ` · rif. ${doc.reference}` : ''}</span>}
          </div>

          <div className="overflow-hidden rounded-md border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-surface">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Prodotto</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">SKU</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-ink-muted">{isTransfer ? 'Richiesti' : 'Quantità'}</th>
                  {isTransfer && <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-ink-muted">Ricevuti</th>}
                  {!isTransfer && <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-ink-muted">Costo unit.</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(doc.items || []).map((it, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-ink">{it.name}</td>
                    <td className="px-3 py-2 text-xs text-ink-faint">{it.sku}</td>
                    <td className="px-3 py-2 text-right">{isTransfer ? it.quantityRequested : it.quantity}</td>
                    {isTransfer && <td className="px-3 py-2 text-right">{it.quantityReceived ?? '-'}</td>}
                    {!isTransfer && <td className="px-3 py-2 text-right">{it.cost != null ? `${it.cost} EUR` : '-'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {doc.notes && <p className="text-sm text-ink-muted">{doc.notes}</p>}

          <dl className="grid grid-cols-2 gap-2 text-xs text-ink-faint sm:grid-cols-3">
            <div><dt className="uppercase tracking-wide">Creato</dt><dd className="text-ink-muted">{fmtDate(doc.createdAt)}</dd></div>
            {isTransfer && doc.shippedAt && <div><dt className="uppercase tracking-wide">Spedito</dt><dd className="text-ink-muted">{fmtDate(doc.shippedAt)}</dd></div>}
            {isTransfer && doc.receivedAt && <div><dt className="uppercase tracking-wide">Ricevuto</dt><dd className="text-ink-muted">{fmtDate(doc.receivedAt)}</dd></div>}
            {!isTransfer && doc.confirmedAt && <div><dt className="uppercase tracking-wide">Confermato</dt><dd className="text-ink-muted">{fmtDate(doc.confirmedAt)}</dd></div>}
          </dl>
        </div>
      )}
    </Modal>
  );
}
